/**
 * PAYMENT SECTION
 * Section "Paiement" réutilisable, intégrée dans ContactDetailPage et ProspectDetailPanel
 * Affiche les payment_cases BPI d'un contact avec badge statut, grille infos, bouton confirmation
 */

import { useMemo, useState } from 'react';
import { Copy, CreditCard, LinkIcon, Mail, RefreshCw } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { toast } from '../ui';
import { usePaymentCase, usePaymentCasesByCrew } from '../../hooks/usePaymentCase';
import { buildBedPaymentLink, useBedActions } from '../../hooks/useBedActions';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import type { PaymentCaseWithRelations } from '../../hooks/usePaymentCase';
import { ConfirmPaymentDialog } from './ConfirmPaymentDialog';
import { RestoreExpiredDialog } from './RestoreExpiredDialog';
import { BedLinkDialog } from './BedLinkDialog';
import { ResendBpiLinkDialog } from './ResendBpiLinkDialog';
import {
  PAYMENT_STATUS_LABELS,
  PAYMENT_STATUS_COLORS,
  PAYMENT_METHOD_LABELS,
  PAYMENT_SCOPE_LABELS,
  PAYMENT_METHOD,
  PAYMENT_SCOPE,
  PAYMENT_STATUS,
} from '../../types/payment';
import type { PaymentAttempt } from '../../types/payment';

// Statuts terminaux d'un dossier BED : un nouveau lien ne doit pas être émis
// par-dessus (la RPC 00130 lève bed_case_already_terminal).
const BED_TERMINAL_STATUSES = new Set<string>([
  PAYMENT_STATUS.PAID,
  PAYMENT_STATUS.EXPIRED,
  PAYMENT_STATUS.FAILED,
]);

// Statuts d'un dossier BPI pour lesquels le mail de paiement peut être renvoyé
// (TICKET-CRM-10) : tant que le participant n'a pas payé et que le dossier
// n'est pas terminal. La route 4LT choisit le template selon le statut.
const RESENDABLE_BPI_STATUSES = new Set<string>([
  PAYMENT_STATUS.DRAFT,
  PAYMENT_STATUS.PENDING_CARD,
  PAYMENT_STATUS.PENDING_TRANSFER,
  PAYMENT_STATUS.PENDING_CHEQUE,
]);

// Somme des tentatives effectivement encaissées (succeeded===true). Sert au
// calcul du « reste à payer » BED multi-CB.
function sumSucceededCents(attempts: PaymentAttempt[]): number {
  return attempts.reduce((acc, a) => (a.succeeded ? acc + a.amount_cents : acc), 0);
}

const MANUAL_CARD_CONFIRMATION_EMAILS = new Set([
  'service.administratif@desertours.com',
  'florence@desertours.com',
]);

type PaymentSectionProps =
  | {
      contactId: string;
      crewId?: never;
      contactName: string;
      compact?: boolean;
      onPaymentConfirmed?: () => void;
    }
  | {
      crewId: string;
      contactId?: never;
      contactName: string;
      compact?: boolean;
      onPaymentConfirmed?: () => void;
    };

function formatDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatAmount(amountCents: number, currency: string): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency || 'EUR',
  }).format(amountCents / 100);
}

function PaymentCaseCard({
  paymentCase,
  contactName,
  compact,
  onConfirmed,
  onBedLinkRegenerated,
}: {
  paymentCase: PaymentCaseWithRelations;
  contactName: string;
  compact?: boolean;
  onConfirmed: () => void;
  // TICKET-BED-15 : refresh seul (PAS onConfirmed — réémettre un lien n'est
  // pas une confirmation de paiement, onPaymentConfirmed ne doit pas partir).
  onBedLinkRegenerated: () => void | Promise<void>;
}) {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const { user } = useAuth();
  const { canManageAssignments } = usePermissions();
  const { generateBedLink } = useBedActions();

  const isBed = paymentCase.scope === PAYMENT_SCOPE.BED;
  const statusColors = PAYMENT_STATUS_COLORS[paymentCase.status];
  // Pour un BED draft (lien émis, en attente du 1er CB), « Lien envoyé » est
  // plus parlant que « En attente de choix ».
  const statusLabel =
    isBed && paymentCase.status === PAYMENT_STATUS.DRAFT
      ? 'Lien envoyé'
      : PAYMENT_STATUS_LABELS[paymentCase.status];
  const scopeLabel = PAYMENT_SCOPE_LABELS[paymentCase.scope];
  const userEmail = user?.email?.toLowerCase() ?? '';
  const canConfirmManualCardForUser = MANUAL_CARD_CONFIRMATION_EMAILS.has(userEmail);
  // Confirmation manuelle offline (virement/chèque) : BPI et BED (TICKET-BED-16).
  // Pour un BED, la RPC confirm_payment_with_timeline (00140) trace un
  // payment_attempt succeeded du restant dû et consomme les bed_tokens actifs.
  const canConfirmOffline =
    paymentCase.status === PAYMENT_STATUS.PENDING_TRANSFER ||
    paymentCase.status === PAYMENT_STATUS.PENDING_CHEQUE;
  const canConfirmManualCard =
    canConfirmManualCardForUser &&
    paymentCase.scope === PAYMENT_SCOPE.BPI &&
    paymentCase.status === PAYMENT_STATUS.PENDING_CARD &&
    paymentCase.method === PAYMENT_METHOD.CARD;
  const canConfirm = canConfirmOffline || canConfirmManualCard;

  // BED multi-CB : reste à payer = montant - somme des tentatives encaissées.
  const succeededCents = isBed ? sumSucceededCents(paymentCase.payment_attempts) : 0;
  const remainingCents = isBed ? Math.max(0, paymentCase.amount_cents - succeededCents) : 0;
  // Sous-commandes (fractions CB) : provider_order_id de type {order_id}-NN.
  const subOrders = isBed
    ? paymentCase.payment_attempts
        .filter((a) => a.method === PAYMENT_METHOD.CARD)
        .slice()
        .sort((a, b) => (a.created_at < b.created_at ? -1 : 1))
    : [];

  // DATA-02 : ré-ouverture d'un dossier auto-expiré, réservée admin/superadmin
  // (la RPC restore_expired_payment_case re-vérifie le rôle côté serveur).
  const canRestore = canManageAssignments() && paymentCase.status === PAYMENT_STATUS.EXPIRED;

  // Lien de paiement BED actif : token non consommé et non expiré le plus
  // récent. Ré-affichable/copiable à tout moment tant que le dossier n'est pas
  // terminal (le lien généré dans BedLinkDialog n'était sinon plus visible
  // après fermeture du dialog).
  const activeBedToken =
    isBed && !BED_TERMINAL_STATUSES.has(paymentCase.status)
      ? (paymentCase.bed_tokens
          .filter((t) => t.used_at === null && new Date(t.expires_at) > new Date())
          .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))[0] ?? null)
      : null;
  // code produit passé pour router la base du tunnel PAR PRODUIT (Lot 2 BED,
  // GAP-2 ; review Victor #3 : products.code, plus le préfixe de l'order_id) ;
  // défaut = tunnel partagé si le produit n'a pas de tunnel dédié.
  const activeBedLink = activeBedToken
    ? buildBedPaymentLink(activeBedToken.token, paymentCase.product_code)
    : null;

  async function handleCopyBedLink() {
    if (!activeBedLink) return;
    try {
      await navigator.clipboard.writeText(activeBedLink);
      toast.success('Lien copié dans le presse-papiers');
    } catch {
      toast.error('Impossible de copier le lien.');
    }
  }

  // TICKET-BED-15 : un BED non-terminal sans token actif (expiré, ou révoqué
  // par une réémission 00136) était une impasse — l'Edge send-bed-link répond
  // 410 no_active_token et le bouton global « Générer un lien BED » est masqué
  // dès qu'un BED existe pour le produit. La RPC create_bed_payment_case est
  // idempotente : rappelée sur ce dossier, elle réémet un token et révoque les
  // anciens. Montant/payeur du dossier repassés tels quels (pas de mutation).
  const canRegenerateBedLink =
    isBed && !BED_TERMINAL_STATUSES.has(paymentCase.status) && !activeBedToken;

  async function handleRegenerateBedLink() {
    setRegenerating(true);
    try {
      const result = await generateBedLink({
        crewId: paymentCase.crew_id ?? undefined,
        contactId: paymentCase.contact_id ?? undefined,
        amountCents: paymentCase.amount_cents,
        payerContactId: paymentCase.payer_contact_id ?? undefined,
      });
      if (!result.success) {
        toast.error(result.error ?? 'Erreur lors de la régénération du lien BED.');
        return;
      }
      toast.success('Nouveau lien généré — les anciens liens de ce dossier sont révoqués.');
      // Refresh seul : le nouveau token apparaît (bloc lien actif + Copier).
      await onBedLinkRegenerated();
    } finally {
      setRegenerating(false);
    }
  }

  // Trouver la dernière tentative payée (pour CB) afin d'avoir le provider_transaction_id
  const paidAttempt = paymentCase.payment_attempts.find(
    (a) => a.provider_transaction_id && a.method === 'card' && a.provider_status === 'PAID'
  );

  const methodLabel = paymentCase.method ? PAYMENT_METHOD_LABELS[paymentCase.method] || '—' : '—';

  const confirmMethodLabel = canConfirmManualCard
    ? 'CB ancienne plateforme'
    : paymentCase.status === PAYMENT_STATUS.PENDING_TRANSFER
      ? 'virement'
      : 'chèque';

  return (
    <>
      <div className="rounded-lg border border-zinc-200 bg-zinc-50/50 p-4">
        {/* Header : nom produit + badge statut */}
        <div className="mb-3 flex items-center justify-between">
          <span className="text-sm font-semibold text-zinc-900">
            {scopeLabel} — {paymentCase.product_name || 'Produit inconnu'}
          </span>
          <Badge
            className={`${statusColors.bg} ${statusColors.text} border ${statusColors.border}`}
          >
            {statusLabel}
          </Badge>
        </div>

        {/* Grille 2 colonnes */}
        <div className={`grid grid-cols-2 gap-x-6 gap-y-2 text-sm ${compact ? 'text-xs' : ''}`}>
          <div>
            <span className="text-xs uppercase tracking-wider text-zinc-400">Montant</span>
            <div className="font-semibold text-zinc-900">
              {formatAmount(paymentCase.amount_cents, paymentCase.currency)}
            </div>
          </div>
          <div>
            <span className="text-xs uppercase tracking-wider text-zinc-400">Méthode</span>
            <div className="text-zinc-700">{methodLabel}</div>
          </div>
          <div>
            <span className="text-xs uppercase tracking-wider text-zinc-400">Référence</span>
            <div className="font-mono text-xs text-zinc-700">{paymentCase.order_id || '—'}</div>
          </div>
          <div>
            <span className="text-xs uppercase tracking-wider text-zinc-400">Payé par</span>
            <div className="text-zinc-700">{paymentCase.payer_name || '—'}</div>
          </div>
          <div>
            <span className="text-xs uppercase tracking-wider text-zinc-400">
              {paymentCase.status === PAYMENT_STATUS.PAID ? 'Date paiement' : 'Date création'}
            </span>
            <div className="text-zinc-700">
              {formatDate(
                paymentCase.status === PAYMENT_STATUS.PAID
                  ? paymentCase.updated_at
                  : paymentCase.created_at
              )}
            </div>
          </div>

          {/* Transaction Sogecommerce (CB payée uniquement) — span 2 colonnes */}
          {paidAttempt?.provider_transaction_id && (
            <div className="col-span-2">
              <span className="text-xs uppercase tracking-wider text-zinc-400">
                Transaction Sogecommerce
              </span>
              <div className="font-mono text-xs text-zinc-700">
                {paidAttempt.provider_transaction_id}
              </div>
            </div>
          )}
        </div>

        {/* BED : reste à payer + détail des fractions CB (multi-CB) */}
        {isBed && (
          <div className="mt-3 border-t border-zinc-200 pt-3">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wider text-zinc-400">Reste à payer</span>
              <span className="text-sm font-semibold text-indigo-700">
                {formatAmount(remainingCents, paymentCase.currency)}
              </span>
            </div>
            {subOrders.length > 0 && (
              <ul className="mt-2 flex flex-col gap-1">
                {subOrders.map((attempt) => (
                  <li
                    key={attempt.id}
                    className="flex items-center justify-between gap-2 text-xs text-zinc-600"
                  >
                    <span className="font-mono">{attempt.provider_order_id || '—'}</span>
                    <span className="flex items-center gap-2">
                      <span>{formatAmount(attempt.amount_cents, paymentCase.currency)}</span>
                      <Badge
                        className={
                          attempt.succeeded
                            ? 'border border-green-200 bg-green-50 text-green-700'
                            : 'border border-amber-200 bg-amber-50 text-amber-700'
                        }
                      >
                        {attempt.succeeded ? 'Encaissé' : 'En attente'}
                      </Badge>
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {/* Lien de paiement actif : ré-affichage + copie après fermeture
                du dialog de génération. */}
            {activeBedToken && activeBedLink && (
              <div className="mt-3">
                <span className="text-xs uppercase tracking-wider text-zinc-400">
                  Lien de paiement (valable jusqu'au {formatDate(activeBedToken.expires_at)})
                </span>
                <div className="mt-1 flex items-center gap-2">
                  <div
                    className="min-w-0 flex-1 truncate rounded-md border border-zinc-200 bg-white px-2 py-1.5 font-mono text-xs text-zinc-600"
                    title={activeBedLink}
                  >
                    {activeBedLink}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleCopyBedLink}
                    className="shrink-0 border-teal-300 text-teal-700 hover:bg-teal-50"
                  >
                    <Copy className="mr-1.5 size-3.5" />
                    Copier
                  </Button>
                </div>
              </div>
            )}

            {/* TICKET-BED-15 : lien expiré/révoqué -> réémission (la RPC
                réutilise le dossier et révoque les anciens tokens). */}
            {canRegenerateBedLink && (
              <div className="mt-3">
                <span className="text-xs uppercase tracking-wider text-zinc-400">
                  Lien de paiement expiré ou révoqué
                </span>
                <div className="mt-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleRegenerateBedLink}
                    disabled={regenerating}
                    className="border-teal-300 text-teal-700 hover:bg-teal-50"
                  >
                    <RefreshCw className="mr-1.5 size-3.5" />
                    {regenerating ? 'Régénération…' : 'Régénérer le lien'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Bouton confirmation manuelle */}
        {canConfirm && (
          <div className="mt-3 border-t border-zinc-200 pt-3">
            <Button
              size="sm"
              onClick={() => setConfirmDialogOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {canConfirmManualCard
                ? 'Confirmer CB ancienne plateforme'
                : `Confirmer réception ${confirmMethodLabel}`}
            </Button>
          </div>
        )}

        {/* Bouton ré-ouverture d'un dossier expiré (admin/superadmin) */}
        {canRestore && (
          <div className="mt-3 border-t border-zinc-200 pt-3">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setRestoreDialogOpen(true)}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              Ré-ouvrir le dossier
            </Button>
          </div>
        )}
      </div>

      {canConfirm && (
        <ConfirmPaymentDialog
          open={confirmDialogOpen}
          onOpenChange={setConfirmDialogOpen}
          paymentCase={paymentCase}
          prospectName={contactName}
          productName={paymentCase.product_name || 'Produit inconnu'}
          onConfirmed={onConfirmed}
        />
      )}

      {canRestore && (
        <RestoreExpiredDialog
          open={restoreDialogOpen}
          onOpenChange={setRestoreDialogOpen}
          paymentCase={paymentCase}
          prospectName={contactName}
          productName={paymentCase.product_name || 'Produit inconnu'}
          onRestored={onConfirmed}
        />
      )}
    </>
  );
}

export function PaymentSection(props: PaymentSectionProps) {
  const { contactName, compact, onPaymentConfirmed } = props;
  const [bedDialogOpen, setBedDialogOpen] = useState(false);
  const [bedDialogCase, setBedDialogCase] = useState<PaymentCaseWithRelations | null>(null);
  const [resendBpiDialogOpen, setResendBpiDialogOpen] = useState(false);

  // Branchement conditionnel : hook par contact OU par crew selon la prop.
  // L'autre hook recoit `undefined` et retourne une liste vide sans requete.
  const byContact = usePaymentCase('contactId' in props ? props.contactId : undefined);
  const byCrew = usePaymentCasesByCrew('crewId' in props ? props.crewId : undefined);
  const { paymentCases, loading, refresh } = 'crewId' in props ? byCrew : byContact;

  // Émission « Lien BED » : la contrainte DB autorise un seul BED par
  // (porteur, produit), quel que soit son statut. La RPC refuse aussi de
  // réémettre un BED terminal. On cherche donc un BPI payé dont le produit
  // n'a encore AUCUN BED, au lieu de bloquer globalement sur un BED d'un autre
  // produit ou de proposer une action impossible après un BED terminal.
  const paidBpiCase = useMemo(() => {
    const productsWithBed = new Set(
      paymentCases.filter((pc) => pc.scope === PAYMENT_SCOPE.BED).map((pc) => pc.product_id)
    );
    return paymentCases.find(
      (pc) =>
        pc.scope === PAYMENT_SCOPE.BPI &&
        pc.status === PAYMENT_STATUS.PAID &&
        !productsWithBed.has(pc.product_id)
    );
  }, [paymentCases]);
  const canGenerateBedLink = !!paidBpiCase;

  // Renvoi du mail de paiement BPI (TICKET-CRM-10) : dossier BPI en attente
  // (non payé, non terminal). Le mail est renvoyé par la route interne 4LT via
  // l'Edge Function send-bpi-link (template choisi selon le statut du case).
  // Comme paidBpiCase plus haut, .find() prend le plus récent si plusieurs
  // cases BPI coexistent (multi-produits, rarissime) — comportement assumé.
  const resendableBpiCase = useMemo(
    () =>
      paymentCases.find(
        (pc) => pc.scope === PAYMENT_SCOPE.BPI && RESENDABLE_BPI_STATUSES.has(pc.status)
      ),
    [paymentCases]
  );

  async function handleConfirmed() {
    await refresh();
    onPaymentConfirmed?.();
  }

  // Appelé par BedLinkDialog à sa FERMETURE (pas à la génération) : le refresh
  // fait apparaître le BED draft -> canGenerateBedLink passe false -> le bouton
  // disparaît. Le dialog lui-même reste monté tant qu'il est ouvert (cf. la
  // condition de montage `canGenerateBedLink || bedDialogOpen` plus bas).
  async function handleBedGenerated() {
    await refresh();
  }

  function handleOpenBedDialog() {
    if (!paidBpiCase) return;

    setBedDialogCase(paidBpiCase);
    setBedDialogOpen(true);
  }

  function handleBedDialogOpenChange(open: boolean) {
    setBedDialogOpen(open);
    if (!open) {
      setBedDialogCase(null);
    }
  }

  const activeBedDialogCase = bedDialogCase ?? paidBpiCase;

  if (loading) {
    return (
      <Card className="p-6">
        <h2 className={`font-semibold text-zinc-900 ${compact ? 'text-sm' : 'text-xl'}`}>
          Paiement
        </h2>
        <p className="mt-2 text-sm text-zinc-400">Chargement...</p>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="mb-4 flex items-center gap-2">
        <CreditCard className="size-5 text-zinc-400" />
        <h2 className={`font-semibold text-zinc-900 ${compact ? 'text-sm' : 'text-xl'}`}>
          Paiement
        </h2>
      </div>

      {paymentCases.length === 0 ? (
        <p className="text-center text-sm text-zinc-400 py-4">
          {'crewId' in props
            ? 'Aucun paiement enregistré pour cet équipage.'
            : 'Aucun paiement enregistré pour ce contact.'}
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {paymentCases.map((pc) => (
            <PaymentCaseCard
              key={pc.id}
              paymentCase={pc}
              contactName={contactName}
              compact={compact}
              onConfirmed={handleConfirmed}
              onBedLinkRegenerated={refresh}
            />
          ))}
        </div>
      )}

      {/* Actions cross-case : émission d'un lien BED (solde restant dû) et/ou
          renvoi du mail de paiement BPI (dossier en attente, TICKET-CRM-10) */}
      {((canGenerateBedLink && paidBpiCase) || resendableBpiCase) && (
        <div className="mt-4 flex flex-wrap gap-2 border-t border-zinc-200 pt-4">
          {canGenerateBedLink && paidBpiCase && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleOpenBedDialog}
              className="border-teal-300 text-teal-700 hover:bg-teal-50"
            >
              <LinkIcon className="mr-2 size-4" />
              Générer un lien BED
            </Button>
          )}
          {resendableBpiCase && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setResendBpiDialogOpen(true)}
              className="border-teal-300 text-teal-700 hover:bg-teal-50"
            >
              <Mail className="mr-2 size-4" />
              Renvoyer le mail de paiement
            </Button>
          )}
        </div>
      )}

      {/* Le dialog reste monté tant qu'il est OUVERT, même si le refresh rend
          canGenerateBedLink false (le BED draft créé pendant la session ne doit
          pas démonter l'écran lien avant copie/envoi). Le bouton ci-dessus, lui,
          reste gaté par canGenerateBedLink. */}
      {(canGenerateBedLink || bedDialogOpen) && activeBedDialogCase && (
        <BedLinkDialog
          open={bedDialogOpen}
          onOpenChange={handleBedDialogOpenChange}
          crewId={'crewId' in props ? props.crewId : undefined}
          contactId={'contactId' in props ? props.contactId : undefined}
          paidBpiCaseId={activeBedDialogCase.id}
          prospectName={contactName}
          productName={activeBedDialogCase.product_name || 'Produit inconnu'}
          onGenerated={handleBedGenerated}
        />
      )}

      {/* Même pattern de montage que BedLinkDialog : le dialog reste monté tant
          que le dossier est renvoyable (l'envoi du mail ne change pas le statut
          du case, donc pas de démontage en cours de session). */}
      {resendableBpiCase && (
        <ResendBpiLinkDialog
          open={resendBpiDialogOpen}
          onOpenChange={setResendBpiDialogOpen}
          paymentCaseId={resendableBpiCase.id}
          payerContactId={resendableBpiCase.payer_contact_id}
          prospectName={contactName}
          productName={resendableBpiCase.product_name || 'Produit inconnu'}
        />
      )}
    </Card>
  );
}
